import { useEffect, useMemo, useState } from 'react';
import { EVENT_META, FEED_TYPES, activeMinutes, addDays, formatClock, formatDuration, localDate, minuteOfDay, type BabyEvent, type DayStat } from '@bb/shared/core';
import { api } from '../api';
import { Icon, useNow } from '../components';
import { avgOf } from '../metrics';
import { useApp } from '../state';

/**
 * «Последние 24 часа»: часы-циферблат на сутки (0 — сверху). Кольца: сон (внешнее), кормления, прогулки;
 * точки по краю — подгузники. Нажатие на кольцо или строку легенды — подробности и сравнение со средним за месяц.
 */

type Cat = 'sleep' | 'feed' | 'walk' | 'diaper';
const H = 3600000;

export interface Window24 {
  from: number;
  to: number;
  sleepMin: number;
  sleepCount: number;
  longestSleepMin: number;
  settleMin: number;
  settleCount: number;
  feedCount: number;
  breastCount: number;
  breastMin: number;
  bottleCount: number;
  bottleMl: number;
  solidCount: number;
  walkMin: number;
  walkCount: number;
  diaperCount: number;
  wetCount: number;
  dirtyCount: number;
  arcs: { cat: Exclude<Cat, 'diaper'>; from: number; to: number; running: boolean }[];
  dots: number[];
}

export function window24(events: readonly BabyEvent[], now: number): Window24 {
  const from = now - 24 * H;
  const w: Window24 = {
    from, to: now, sleepMin: 0, sleepCount: 0, longestSleepMin: 0, settleMin: 0, settleCount: 0, feedCount: 0, breastCount: 0, breastMin: 0, bottleCount: 0, bottleMl: 0,
    solidCount: 0, walkMin: 0, walkCount: 0, diaperCount: 0, wetCount: 0, dirtyCount: 0, arcs: [], dots: [],
  };
  for (const e of events) {
    if (e.type === 'sleep' && e.payload.settling) continue; // укладывание — ещё не сон
    // Часы телефона и сервера могут расходиться на минуту-другую: запись «чуть из будущего» считаем текущей.
    const raw = Date.parse(e.startedAt);
    const s = raw > now && raw - now < 5 * 60000 ? now : raw;
    const timed = EVENT_META[e.type].timed;
    const end = e.endedAt ? Date.parse(e.endedAt) : timed ? now : s;
    const inWin = s >= from && s <= now;
    if (timed && (end <= from || s > now)) continue;
    if (!timed && !inWin) continue;
    const a = Math.max(s, from);
    const b = Math.min(end, now);
    const over = Math.max(0, b - a) / 60000;
    if (e.type === 'sleep') {
      w.sleepMin += over;
      if (inWin) w.sleepCount += 1;
      w.longestSleepMin = Math.max(w.longestSleepMin, (end - s) / 60000);
      if (inWin && typeof e.payload.settleStartedAt === 'string') {
        w.settleMin += Math.max(0, (s - Date.parse(e.payload.settleStartedAt)) / 60000);
        w.settleCount += 1;
      }
      w.arcs.push({ cat: 'sleep', from: a, to: b, running: !e.endedAt });
    } else if (e.type === 'walk') {
      w.walkMin += over;
      if (inWin) w.walkCount += 1;
      w.arcs.push({ cat: 'walk', from: a, to: b, running: !e.endedAt });
    } else if (FEED_TYPES.includes(e.type)) {
      if (inWin) {
        w.feedCount += 1;
        if (e.type === 'breast') {
          w.breastCount += 1;
          w.breastMin += activeMinutes(e, now);
        }
        if (e.type === 'bottle') {
          w.bottleCount += 1;
          w.bottleMl += Number(e.payload.ml) || 0;
        }
        if (e.type === 'solid') w.solidCount += 1;
      }
      // Короткое кормление всё равно видно: минимум 10 минут дуги.
      w.arcs.push({ cat: 'feed', from: a, to: Math.max(b, a + 10 * 60000), running: !e.endedAt && timed });
    } else if (e.type === 'diaper' && inWin) {
      w.diaperCount += 1;
      if (e.payload.kind === 'wet' || e.payload.kind === 'mixed') w.wetCount += 1;
      if (e.payload.kind === 'dirty' || e.payload.kind === 'mixed') w.dirtyCount += 1;
      w.dots.push(s);
    }
  }
  return w;
}

/** Средние за последние 30 полных дней — для сравнения. */
function useMonthAvg() {
  const { child, family } = useApp();
  const today = localDate(Date.now(), family.tz);
  const [days, setDays] = useState<DayStat[] | null>(null);
  useEffect(() => {
    let alive = true;
    api.stats(child.id, addDays(today, -30), addDays(today, -1)).then(
      (s) => alive && setDays(s.days.filter((d) => d.date >= child.birthDate)),
      () => alive && setDays([]),
    );
    return () => {
      alive = false;
    };
  }, [child.id, today, child.birthDate]);
  return days;
}

const LANES: { cat: Cat; label: string }[] = [
  { cat: 'sleep', label: 'Сон' },
  { cat: 'feed', label: 'Кормления' },
  { cat: 'walk', label: 'Прогулки' },
  { cat: 'diaper', label: 'Подгузники' },
];

export function Day24() {
  const ctx = useApp();
  // «Сейчас» берём в момент пересчёта: новая запись видна сразу, а не после очередного шага таймера.
  const tick = useNow(30000);
  const tz = ctx.family.tz;
  const w = useMemo(() => window24(ctx.recent?.events ?? [], Date.now()), [ctx.recent, tick]);
  const now = w.to;
  const [open, setOpen] = useState<Cat | null>(null);
  const month = useMonthAvg();
  const x = (t: number) => ((t - w.from) / (w.to - w.from)) * 100;

  // Подписи шкалы — круглые часы каждые 6 часов внутри окна.
  const ticks: { pos: number; label: string }[] = [];
  const firstHour = w.from + (60 - (minuteOfDay(w.from, tz) % 60)) * 60000;
  for (let t = firstHour; t < w.to; t += H) {
    const h = Math.round(minuteOfDay(t, tz) / 60) % 24;
    // Возле правого края — подпись «сейчас», отметку туда не ставим, чтобы не слипались.
    if (h % 6 === 0 && x(t) > 6 && x(t) < 86) ticks.push({ pos: x(t), label: `${String(h).padStart(2, '0')}:00` });
  }

  const value: Record<Cat, string> = {
    sleep: formatDuration(w.sleepMin),
    feed: `${w.feedCount} ${w.feedCount === 1 ? 'раз' : w.feedCount >= 2 && w.feedCount <= 4 ? 'раза' : 'раз'}`,
    walk: w.walkMin ? formatDuration(w.walkMin) : 'не было',
    diaper: `${w.diaperCount} шт`,
  };
  const sub: Record<Cat, string> = {
    sleep: w.sleepCount ? `засыпал ${w.sleepCount} раз` : '',
    feed: [w.breastCount ? `грудь ${w.breastCount}` : '', w.bottleMl ? `${Math.round(w.bottleMl)} мл` : ''].filter(Boolean).join(' · '),
    walk: w.walkCount ? `${w.walkCount} раз` : '',
    diaper: w.diaperCount ? `мокр. ${w.wetCount} · гряз. ${w.dirtyCount}` : '',
  };

  return (
    <section className="card stack" aria-label="Последние 24 часа">
      <div className="row">
        <span className="section-title grow">Последние 24 часа</span>
        <span className="tiny muted">
          с {formatClock(w.from, tz)} вчера
        </span>
      </div>

      {/* Горизонтальная шкала: строка на каждый вид, слева — 24 часа назад, справа — сейчас */}
      <div className="lanes" role="img" aria-label="Когда малыш спал, ел, гулял и когда меняли подгузник за последние 24 часа">
        <div className="lane-scale" aria-hidden="true">
          {ticks.map((tk) => (
            <span key={tk.label + tk.pos} style={{ left: `${tk.pos}%` }}>
              {tk.label}
            </span>
          ))}
          <span className="now" style={{ left: '100%' }}>
            сейчас
          </span>
        </div>
        {LANES.map((l) => (
          <button key={l.cat} type="button" className={`lane${open && open !== l.cat ? ' dim' : ''}`} onClick={() => setOpen(open === l.cat ? null : l.cat)} aria-label={`${l.label}: ${value[l.cat]}`}>
            <span className="lane-label">{l.label}</span>
            <span className="lane-track">
              {ticks.map((tk) => (
                <i key={tk.pos} className="lane-grid" style={{ left: `${tk.pos}%` }} />
              ))}
              {l.cat === 'diaper'
                ? w.dots.map((t, i) => <i key={i} className="lane-dot" style={{ left: `${x(t)}%`, background: 'var(--c-diaper)' }} />)
                : w.arcs
                    .filter((a) => a.cat === l.cat)
                    .map((a, i) => (
                      <i
                        key={i}
                        className={`lane-seg${a.running ? ' running' : ''}`}
                        style={{ left: `${x(a.from)}%`, width: `${Math.max(0.8, x(a.to) - x(a.from))}%`, background: `var(--c-${l.cat})` }}
                      />
                    ))}
            </span>
          </button>
        ))}
      </div>

      {/* Кнопки-итоги: нажатие разворачивает статистику под ними */}
      <div className="sum-grid" role="group" aria-label="Итоги за 24 часа">
        {LANES.map((l) => (
          <button
            key={l.cat}
            type="button"
            className={`sum-btn cat-${l.cat}`}
            aria-expanded={open === l.cat}
            onClick={() => setOpen(open === l.cat ? null : l.cat)}
            data-testid={`l24-${l.cat}`}
          >
            <span className="row" style={{ gap: 6 }}>
              <span className="label grow">{l.label}</span>
              <Icon name={open === l.cat ? 'up' : 'down'} size={14} />
            </span>
            <span className="value">{value[l.cat]}</span>
            {sub[l.cat] && <span className="tiny">{sub[l.cat]}</span>}
          </button>
        ))}
      </div>
      {open ? (
        <>
          <Details cat={open} w={w} month={month} />
          <button type="button" className="link-btn" onClick={() => setOpen(null)}>
            Свернуть
          </button>
        </>
      ) : (
        <p className="tiny muted" style={{ textAlign: 'center' }}>
          Нажмите на вид — разверну подробности и сравню со средним за месяц
        </p>
      )}
      <TodayStrip />
    </section>
  );
}

/** Строка сравнения: две горизонтальные полосы в одной шкале — «за 24 часа» и «обычно». */
function Cmp({ label, now, avg, fmt, cat }: { label: string; now: number; avg: number | null; fmt: (v: number) => string; cat: Cat }) {
  const max = Math.max(now, avg ?? 0, 1e-9);
  const diff = avg == null ? null : now - avg;
  const same = diff != null && Math.abs(diff) <= Math.max(0.5, Math.abs(avg ?? 0) * 0.05);
  return (
    <div className="cmp">
      <div className="row" style={{ gap: 8 }}>
        <span className="grow small">{label}</span>
        <span className="tiny muted">{avg == null ? '' : same ? 'как обычно' : diff! > 0 ? 'больше обычного' : 'меньше обычного'}</span>
      </div>
      <div className="cmp-bar">
        <span className="cmp-k">24 ч</span>
        <span className="bd-track grow">
          <span style={{ width: `${(now / max) * 100}%`, background: `var(--c-${cat})` }} />
        </span>
        <b>{fmt(now)}</b>
      </div>
      {avg != null && (
        <div className="cmp-bar">
          <span className="cmp-k">обычно</span>
          <span className="bd-track grow">
            <span style={{ width: `${(avg / max) * 100}%`, background: 'var(--muted)' }} />
          </span>
          <span className="small">{fmt(avg)}</span>
        </div>
      )}
    </div>
  );
}

function Details({ cat, w, month }: { cat: Cat; w: Window24; month: DayStat[] | null }) {
  const a = (f: (d: DayStat) => number) => (month ? avgOf(month, f) : null);
  const n = (v: number) => (Math.round(v * 10) / 10).toLocaleString('ru-RU');
  const dur = (v: number) => formatDuration(v);
  return (
    <div className="detail stack" data-testid="d24">
      <span className="small" style={{ fontWeight: 700 }}>
        {cat === 'sleep' ? 'Сон' : cat === 'feed' ? 'Кормления' : cat === 'walk' ? 'Прогулки' : 'Подгузники'}: последние 24 часа и среднее за 30 дней
      </span>
      {cat === 'sleep' && (
        <>
          <Cmp cat={cat} label="Всего" now={w.sleepMin} avg={a((d) => d.sleepMin)} fmt={dur} />
          <Cmp cat={cat} label="Засыпаний" now={w.sleepCount} avg={a((d) => d.sleepCount)} fmt={n} />
          <Cmp cat={cat} label="Самый длинный сон" now={w.longestSleepMin} avg={a((d) => d.longestSleepMin)} fmt={dur} />
          {w.settleCount > 0 && (
            <Cmp
              cat={cat}
              label={`Укладывание (${w.settleCount} раз), в среднем`}
              now={w.settleMin / w.settleCount}
              avg={(() => {
                const ds = (month ?? []).filter((d) => d.settleCount > 0);
                const n = ds.reduce((s, d) => s + d.settleCount, 0);
                return n ? ds.reduce((s, d) => s + d.settleMin, 0) / n : null;
              })()}
              fmt={dur}
            />
          )}
        </>
      )}
      {cat === 'feed' && (
        <>
          <Cmp cat={cat} label="Кормлений" now={w.feedCount} avg={a((d) => d.feedCount)} fmt={n} />
          <Cmp cat={cat} label="Грудь, раз" now={w.breastCount} avg={a((d) => d.breastCount)} fmt={n} />
          <Cmp cat={cat} label="Грудь, время" now={w.breastMin} avg={a((d) => d.breastMin)} fmt={dur} />
          <Cmp cat={cat} label="Бутылочка, мл" now={w.bottleMl} avg={a((d) => d.bottleMl)} fmt={(v) => `${Math.round(v)} мл`} />
          {(w.solidCount > 0 || (a((d) => d.solidCount) ?? 0) > 0) && <Cmp cat={cat} label="Прикорм" now={w.solidCount} avg={a((d) => d.solidCount)} fmt={n} />}
        </>
      )}
      {cat === 'walk' && (
        <>
          <Cmp cat={cat} label="На улице" now={w.walkMin} avg={a((d) => d.walkMin)} fmt={dur} />
          <Cmp cat={cat} label="Прогулок" now={w.walkCount} avg={a((d) => d.walkCount)} fmt={n} />
        </>
      )}
      {cat === 'diaper' && (
        <>
          <Cmp cat={cat} label="Всего" now={w.diaperCount} avg={a((d) => d.diaperCount)} fmt={n} />
          <Cmp cat={cat} label="Мокрых" now={w.wetCount} avg={a((d) => d.wetCount)} fmt={n} />
          <Cmp cat={cat} label="Грязных" now={w.dirtyCount} avg={a((d) => d.dirtyCount)} fmt={n} />
        </>
      )}
      {month && month.filter((d) => d.hasData).length < 3 && <span className="tiny muted">Сравнение станет точнее, когда наберётся несколько дней записей.</span>}
    </div>
  );
}

/** «Сегодня по часам» — с полуночи до сейчас. */
function TodayStrip() {
  const ctx = useApp();
  const tick = useNow(60000);
  const tz = ctx.family.tz;
  const now = Math.max(tick, ctx.recent ? Date.now() : tick);
  const startOfDay = now - minuteOfDay(now, tz) * 60000;
  const segs = useMemo(() => {
    const out: { cat: string; from: number; to: number }[] = [];
    for (const e of ctx.recent?.events ?? []) {
      const s = Date.parse(e.startedAt);
      const meta = EVENT_META[e.type];
      if (meta.category === 'other') continue;
      const end = e.endedAt ? Date.parse(e.endedAt) : meta.timed ? now : s;
      if (end < startOfDay && !(s >= startOfDay)) continue;
      const a = Math.max(0, (s - startOfDay) / 60000);
      const b = meta.timed ? Math.max(a + 8, (Math.min(end, now) - startOfDay) / 60000) : a + 10;
      if (meta.timed ? end > startOfDay : s >= startOfDay) out.push({ cat: meta.category, from: a, to: b });
    }
    return out;
  }, [ctx.recent, now, startOfDay]);
  return (
    <div className="stack" style={{ gap: 6, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <span className="section-title">Сегодня по часам</span>
      <div className="strip big">
        {segs.map((s, i) => (
          <span key={i} style={{ left: `${(s.from / 1440) * 100}%`, width: `${Math.max(0.6, ((s.to - s.from) / 1440) * 100)}%`, background: `var(--c-${s.cat})` }} />
        ))}
        <span className="now-mark" style={{ left: `${(minuteOfDay(now, tz) / 1440) * 100}%` }} />
      </div>
      <div className="strip-scale">
        {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((h) => (
          <span key={h}>{h}</span>
        ))}
      </div>
    </div>
  );
}
